import { IDiversitySubmitState, IDiversityRow } from '../types/Diversity.types';
import { IProductsSubmitState } from '../types/ProductsServices.types';
import { newAddressKeyCode } from '../types/CompanySelection.types';
import { QUESTIONID, ANNUAL_SALE } from '../types/CompanyConfig.types';

interface BusinessItem {
  [key: number]: string;
}

interface IPersonData {
  locale: string;
  userName: string;
  firstName: string;
  lastName: string;
  salutation?: string;
  businessTitle: string;
  workPhone: string;
  password: string;
  timeZone: string;
  emailAddress: string;
  personKey?: number;
  personStatusCode?: string;
}

interface IBidEventData {
  offeringKey?: number;
  reasonForRequest?: string;
}

interface IAddressData {
  stateProvince: string;
  city: string;
  siteTypeCode: string;
  siteName: string;
  line1: string;
  line2: string;
  country: string;
  phone: string;
  siteDescription: string;
  postalCode: string;
  addressTypeCode: string;
  addressKey?: number;
}

interface RegistrationData {
  companyKey: string;
  person: IPersonData;
  company: {
    companyName: string;
    supplierCompanyKey: number;
    website: string;
    dunns: string;
    fedTaxId: string;
  };
  address: IAddressData;
  surveyName: {
    businessInfo: BusinessItem[];
    businessBio: BusinessItem[];
  };
  diversity: IDiversitySubmitState;
  products: {
    selectedProducts: IProductsSubmitState;
  };
  bidEvent?: IBidEventData;
  refPersonKey?: number;
  portalId?: number;
  contractId?: number;
  localeForEmail?: string;
}

/**
 * Builds the person data
 * @param data The object with the data to submit
 */
const buildPersonData = (data: any): IPersonData => {
  const { salutation } = data.userInfo;
  let locale;
  switch (data.language) {
    case 'fr':
      locale = 'fr_CA';
      break;
    case 'es':
      locale = 'en_US'; //TODO: Change this to 'es' after the eip is localized after registration!
      break;
    default:
      locale = 'en_US';
      break;
  }
  let person: IPersonData = {
    locale,
    userName: data.confirmation.username.trim(),
    firstName: data.userInfo.firstName,
    lastName: data.userInfo.lastName,
    ...(salutation && salutation.trim().length > 0 ? { salutation: salutation } : {}),
    businessTitle: data.userInfo.jobTitle,
    workPhone: data.userInfo.workPhone,
    password: data.confirmation.password,
    timeZone: data.userInfo.timeZone,
    emailAddress: data.userInfo.emailAddress
  };

  if (data.userInfo.personKey) {
    person = {
      ...person,
      personKey: Number(data.userInfo.personKey),
      personStatusCode: data.userInfo.status
    };
  }

  return person;
};

/**
 * Builds the bidEvent data
 * @param requestAccess The object with the request access data to submit
 */
const buildBidEventData = (requestAccess: any): IBidEventData => {
  let bidEvent: IBidEventData = {};
  if (typeof requestAccess.offeringKey === 'string' && requestAccess.offeringKey.trim().length > 0) {
    bidEvent = {
      offeringKey: Number(requestAccess.offeringKey)
    };
  }

  if (typeof requestAccess.requestAccessReason === 'string' && requestAccess.requestAccessReason.trim().length > 0) {
    bidEvent = {
      ...bidEvent,
      reasonForRequest: requestAccess.requestAccessReason
    };
  }

  return bidEvent;
};

/**
 * Builds the address data
 * @param data The object with the company selection data to submit
 */
const buildAddressData = (companySelection: any): IAddressData => {
  let address: IAddressData = {
    city: companySelection.city,
    stateProvince: companySelection.stateProvince,
    siteTypeCode: companySelection.siteTypeCode ? companySelection.siteTypeCode : 'COR',
    siteName: companySelection.siteName,
    line1: companySelection.line1,
    line2: companySelection.line2,
    country: companySelection.country,
    postalCode: companySelection.postalcode,
    phone: companySelection.phone,
    siteDescription: companySelection.siteName,
    addressTypeCode: 'BUS'
  };

  if (typeof companySelection.selectLocation.addresskey !== 'undefined' && companySelection.selectLocation.addresskey !== null) {
    if (companySelection.selectLocation.addresskey !== newAddressKeyCode) {
      address.addressKey = companySelection.selectLocation.addresskey;
    }
  }
  
  return address;
};

/**
 * Builds the registration data object for a form submission
 * @param data The Form data state
 */
export const buildRegistrationData = (data: any, companyKey: string): RegistrationData => {

  data.diversity.uploadCertificates= data.diversity.uploadCertificates.map((diversityFiles: IDiversityRow) => {
    if(diversityFiles.signedUrl?.includes('cloudfront')) {
      return diversityFiles
    }
    return null;
  })
  .filter((n:any) => n);//Remove the nulls from the removal above

  let localeForEmail = 'en_US';
  switch (data.language) {
    case 'fr':
      localeForEmail = 'fr_CA';
      break;
    case 'es':
      localeForEmail = 'es';
      break;
    default:
      localeForEmail = 'en_US';
      break;
  }
  let portalId;
  if (typeof data.requestAccess.portalId === 'string' && data.requestAccess.portalId.trim().length > 0) {
    portalId = Number(data.requestAccess.portalId);
  }
  let contractId;
  if (typeof data.requestAccess.contractId === 'string' && data.requestAccess.contractId.trim().length > 0) {
    contractId = Number(data.requestAccess.contractId);
  }
  let refPersonKey
  if (typeof data.requestAccess.refPersonKey === 'string' && data.requestAccess.refPersonKey.trim().length > 0) {
    refPersonKey = Number(data.requestAccess.refPersonKey);
  }
  let registrationData: RegistrationData = {
    companyKey,
    refPersonKey,
    portalId,
    contractId,
    localeForEmail,
    person: buildPersonData(data),
    company: {
      companyName: data.companySelection.company.name,
      supplierCompanyKey: data.companySelection.company.companyKey,
      website: data.businessInfo.companyWebsite,
      dunns: data.businessInfo.dunsNumber,
      fedTaxId: data.businessInfo.companyTaxIdNumber
    },
    address: buildAddressData(data.companySelection),
    surveyName: {
      businessInfo: [
        {
          [QUESTIONID.companyLegalName]: data.businessInfo.companyLegalName,
          [QUESTIONID.companyDoingBusinessAs]: data.businessInfo.companyDoingBusinessAs,
          [QUESTIONID.companyParentCompany]: data.businessInfo.companyParentCompany,
          [QUESTIONID.companyPhoneNumber]: data.businessInfo.companyPhoneNumber,
          [QUESTIONID.companyWebsite]: data.businessInfo.companyWebsite,
          [QUESTIONID.companyEmailAddress]: data.businessInfo.companyEmailAddress,
          [QUESTIONID.companyTaxIdNumber]: data.businessInfo.companyTaxIdNumber,
          [QUESTIONID.dunsNumber]: data.businessInfo.dunsNumber,
          [QUESTIONID.primaryNAICSCode]: data.businessInfo.primaryNAICSCode,
          [QUESTIONID.secondaryNAICSCode]: data.businessInfo.secondaryNAICSCode
        }
      ],
      businessBio: [
        {
          [QUESTIONID.yearCompanyWasFounded]: data.businessBio.yearCompanyWasFounded,
          [QUESTIONID.numberOfPermanentEmployees]: data.businessBio.numberOfPermanentEmployees,
          [QUESTIONID.unionizedCompany]: data.businessBio.unionizedCompany && data.businessBio.unionizedCompany  === 'yes' ? 'Yes' : 'No',
          [QUESTIONID.supplierCompanyOwnership]: data.businessBio.supplierCompanyOwnership,
          ...annualRevenuePayload(data)
        }
      ]
    },
    diversity: {
      uploadCertificates: data.diversity.uploadCertificates
    },
    products: {
      selectedProducts: data.productsServices.productsAddedList.filter((v: { itemKey: any; }, i: any, a: any[]) => a.findIndex(t => (t.itemKey === v.itemKey)) === i)
    },
    bidEvent: buildBidEventData(data.requestAccess)
  };

  return registrationData;
};


const annualRevenuePayload = (data: any) => {
  const date = new Date();
  const currentYear = date.getFullYear();
  let returnAnnualRevenuePayloadObj: any = {};
  for (let index = 1; index <= 3; index++) {
    const newAnnualRevenue = new Date(currentYear - index, date.getMonth(), date.getDate()).getFullYear();
    returnAnnualRevenuePayloadObj[`${ANNUAL_SALE[newAnnualRevenue]}`] = data.businessInfo[`annualSale${index}`]
      ? data.businessInfo[`annualSale${index}`]
      : 0;
  }
  return returnAnnualRevenuePayloadObj;
};


/**
 * Sort handler for an `array.sort` that sorts alphabetically in ascending or descending order
 * @param item1 The first item to compare
 * @param item2 The second item to compare
 * @param sort The sort method `asc` or `desc`
 */
export const sortHandlerAlpha = (item1: string, item2: string, sort: 'asc' | 'desc' = 'asc') => {
  if (sort === 'asc') {
    return item1.toLocaleLowerCase().localeCompare(item2.toLocaleLowerCase());
  } else {
    const compare = item1.toLocaleLowerCase().localeCompare(item2.toLocaleLowerCase());
    return compare * -1;
  }
};

/**
 * Sort handler for an `array.sort` that sorts numbers in ascending or descending order
 * @param item1 The first item to compare
 * @param item2 The second item to compare
 * @param sort The sort method `asc` or `desc`
 */
export const sortHandlerNumber = (item1: number, item2: number, sort: 'asc' | 'desc' = 'desc') => {
  if (sort === 'asc') {
    return item1 - item2
  } else {
    return item2 - item1
  }
}